import React, { useState, useEffect, useCallback } from "react";
import { makeAuthenticatedRequest } from "../../utils/apiRequest";
import { useAuth } from "../../contexts/authContext";

const OtherExpensesPopup = ({ isOpen, onClose, itineraryId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
// Track currency per expense (default: $)
const [currencyMap, setCurrencyMap] = useState({});
// Store USD rate for conversion (default 83 if not fetched)
const [usdRate, setUsdRate] = useState(83); // fallback to 83 if not found
// Helper to get tax percentage from API response
const getTaxPercentage = () => {
  const itineraryData = apiResponseData?.data?.[0] || apiResponseData;
  if (itineraryData && itineraryData.taxes && itineraryData.taxes.length > 0) {
    return itineraryData.taxes[0].tax_percentage || 0;
  }
  return 0;
};
  const [apiResponseData, setApiResponseData] = useState(null);

  // Sample data to display initially or as fallback
  const getSampleData = () => {
    // return [
    //   {
    //     day: "01",
    //     date: "(20XX-XX-XX)",
    //     expenses: [
    //       { type: "DJ and Dance", amount: 250 },
    //       { type: "Liquor", amount: 300 }
    //     ]
    //   },
    //   {
    //     day: "02",
    //     date: "(20XX-XX-XX)",
    //     expenses: [
    //       { type: "DJ and Dance", amount: 250 },
    //       { type: "Liquor", amount: 300 }
    //     ]
    //   }
    // ];
  };



  // Define fetchOtherExpenses with useCallback to prevent recreation on each render
  const fetchOtherExpenses = useCallback(async () => {
    if (!token || !itineraryId) {
      // Use sample data if no token or itineraryId
      setExpenses(getSampleData());
      return;
    }
    
    console.log('OtherExpensesPopup: Starting to fetch other expenses data...');
    console.log('OtherExpensesPopup: Using itinerary ID:', itineraryId);
    
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ itinerary_id: itineraryId }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('OtherExpensesPopup: API response received:', responseData);
      
      // Store the full response data in state for later use
      setApiResponseData(responseData);
      
      // The API response has data nested inside a 'data' array
      const itineraryData = responseData.data && responseData.data[0] ? responseData.data[0] : responseData;
      console.log('OtherExpensesPopup: Extracted itinerary data:', itineraryData);

      // --- Extract USD rate from rates array in itinerary JSON ---
      if (Array.isArray(itineraryData.rates)) {
        const usdObj = itineraryData.rates.find(c => c.currency_name && c.currency_name.includes("USD"));
        if (usdObj && usdObj.value) {
          setUsdRate(Number(usdObj.value));
        } else {
          setUsdRate(83); // fallback if not found
        }
      } else {
        setUsdRate(83); // fallback if rates array missing
      }
      
      // Process the data from the itinerary_cost array which contains other expenses information
      if (itineraryData.itinerary_cost && Array.isArray(itineraryData.itinerary_cost)) {
        console.log('OtherExpensesPopup: Valid other expenses data found in itinerary_cost:', itineraryData.itinerary_cost);
        
        // Process the API response data to match our component's expected format
        const formattedData = [];
        const newCurrencyMap = {};
        
        // Loop through each day in itinerary_cost
        itineraryData.itinerary_cost.forEach((dayData, dayIndex) => {
          if (dayData.day) {
            // Get the day number and date
            const dayNumber = dayData.day?.replace('Day ', '') || '01';
            const dayDate = dayData.date || '(20XX-XX-XX)';
            
            // Support both 'Other_Expenses' and 'other_expenses' keys for compatibility
            const otherExpenses = dayData.Other_Expenses || dayData.other_expenses || [];
            
            // If there's no other expenses data, use sample data
            const expenseItems = otherExpenses.length > 0 ? otherExpenses : [
              { type: "DJ and Dance", amount: 0 },
              { type: "Liquor", amount: 0 }
            ];
            
            // Set currencyMap for each expense
            expenseItems.forEach((e, expenseIndex) => {
              if (e.currency === 'RS') {
                newCurrencyMap[`${formattedData.length}-${expenseIndex}`] = 'RS';
              } else {
                newCurrencyMap[`${formattedData.length}-${expenseIndex}`] = '$';
              }
            });
            
            formattedData.push({
              day: dayNumber,
              date: dayDate,
              expenses: expenseItems.map(e => ({
                type: e.type || "Expense",
                // Store both baseAmount (without tax) and amount (displayed)
                baseAmount: typeof e.baseAmount === "number"
                  ? e.baseAmount
                  : (typeof e.amount === "number" ? e.amount : 0),
                amount: typeof e.amount === "number" ? e.amount : 0,
                taxIncluded: typeof e.taxIncluded === "boolean" ? e.taxIncluded : false
              }))
            });
          }
        });
        
        // If no valid data was found, use sample data
        if (formattedData.length === 0) {
          setExpenses(getSampleData());
        } else {
          setExpenses(formattedData);
          setCurrencyMap(newCurrencyMap);
        }
      } else {
        // Fallback to sample data if no valid data structure is found
        console.log('OtherExpensesPopup: No valid other expenses data found, using sample data');
        setExpenses(getSampleData());
      }
    } catch (err) {
      console.error('Error fetching other expenses data:', err);
      setError(err.message);
      // Use sample data on error
      setExpenses(getSampleData());
    } finally {
      setLoading(false);
    }
  }, [token, itineraryId]);

  // Load data when component mounts and isOpen changes
  useEffect(() => {
    if (isOpen) {
      fetchOtherExpenses();
    }
  }, [isOpen, fetchOtherExpenses]);

  // Calculate the total amount across all days and expense types
const calculateTotal = () => {
  let total = 0;
  expenses.forEach((day, dayIndex) => {
    day.expenses.forEach((expense, expenseIndex) => {
      const key = `${dayIndex}-${expenseIndex}`;
      const currency = currencyMap[key] || '$';
      let amount = 0;
      if (currency === 'RS') {
        // Use enteredRS if available, otherwise convert from USD
        const rsVal = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) : (parseFloat(expense.amount) * usdRate);
        if (!isNaN(rsVal)) {
          amount = rsVal / usdRate;
        }
      } else {
        amount = parseFloat(expense.amount);
      }
      if (!isNaN(amount)) {
        total += amount;
      }
    });
  });
  return total;
};

  // Format numbers to 2 decimal places with safety check
  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return parseFloat(value).toFixed(2);
  };

  // Track which input cell is being edited: { day: index, expense: index } | null
  const [editingCell, setEditingCell] = useState(null);

  // Helper: handle Enter key to blur input
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Handle currency toggle with value conversion
const handleCurrencyToggle = (dayIndex, expenseIndex) => {
  const updatedExpenses = [...expenses];
  const key = `${dayIndex}-${expenseIndex}`;
  const current = currencyMap[key] || '$';
  const expense = updatedExpenses[dayIndex]?.expenses[expenseIndex];
  if (expense) {
    const taxPercentage = getTaxPercentage();
    const isTaxIncluded = expense.taxIncluded;
    if (current === '$') {
      // $ → Rs: convert current USD value to Rs and update both fields
      const usdVal = parseFloat(expense.amount) || 0;
      const rsVal = (usdVal * usdRate).toFixed(2);
      expense.enteredRS = rsVal;
      // Keep USD value in .amount
    } else {
      // Rs → $: convert current RS value to USD and update both fields
      const rsVal = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) || 0 : 0;
      const usdVal = (rsVal / usdRate).toFixed(2);
      expense.amount = usdVal;
      // Remove enteredRS
      delete expense.enteredRS;
    }
    setExpenses(updatedExpenses);
    setCurrencyMap((prev) => ({ ...prev, [key]: current === '$' ? 'RS' : '$' }));
  }
};

  // Handler for changing expense amount
const handleAmountChange = (dayIndex, expenseIndex, newAmount) => {
  const updatedExpenses = [...expenses];
  if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
    const expense = updatedExpenses[dayIndex].expenses[expenseIndex];
    const key = `${dayIndex}-${expenseIndex}`;
    const selectedCurrency = currencyMap[key] || '$';
    let amount = newAmount === '' ? '' : parseFloat(newAmount);
    if (selectedCurrency === 'RS') {
      // Store the RS value as enteredRS, and also update USD amount
      expense.enteredRS = amount;
      if (amount === '' || isNaN(amount)) {
        expense.amount = '';
      } else {
        expense.amount = (amount / usdRate).toFixed(2);
      }
    } else {
      // Store USD value as amount, clear enteredRS
      expense.amount = amount;
      delete expense.enteredRS;
    }
    const taxPercentage = getTaxPercentage();
    if (expense.taxIncluded) {
      // amount is total, calculate base and tax
      const amountInUSD = selectedCurrency === 'RS' ? (amount === '' || isNaN(amount) ? '' : amount / usdRate) : amount;
      const baseAmount = amountInUSD === '' ? '' : amountInUSD / (1 + taxPercentage / 100);
      expense.baseAmount = baseAmount;
      expense.taxAmount = (amountInUSD !== '' && baseAmount !== '' ? (amountInUSD - baseAmount).toFixed(2) : '');
    } else {
      // amount is base, calculate total
      const amountInUSD = selectedCurrency === 'RS' ? (amount === '' || isNaN(amount) ? '' : amount / usdRate) : amount;
      expense.baseAmount = amountInUSD;
      delete expense.taxAmount;
    }
    setExpenses(updatedExpenses);
  }
};

  // Handle tax checkbox toggle
  const handleTaxToggle = (dayIndex, expenseIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
      const expense = updatedExpenses[dayIndex].expenses[expenseIndex];
      const key = `${dayIndex}-${expenseIndex}`;
      const selectedCurrency = currencyMap[key] || '$';
      const taxPercentage = getTaxPercentage();
      
      if (expense.taxIncluded) {
        // Was previously true, now will be false (removing tax)
        expense.taxIncluded = false;
        // Remove tax using mathematically correct method
        const withoutTax = parseFloat(expense.amount || 0) / (1 + taxPercentage / 100);
        expense.amount = withoutTax.toFixed(2);
        expense.baseAmount = withoutTax;
        
        // Update RS value if currency is RS
        if (selectedCurrency === 'RS' && expense.enteredRS !== undefined) {
          const rsWithoutTax = parseFloat(expense.enteredRS || 0) / (1 + taxPercentage / 100);
          expense.enteredRS = rsWithoutTax.toFixed(2);
        }
      } else {
        // Was previously false, now will be true (adding tax)
        expense.taxIncluded = true;
        expense.amount = (parseFloat(expense.amount || 0) * (1 + taxPercentage / 100)).toFixed(2);
        // baseAmount remains unchanged
        
        // Update RS value if currency is RS
        if (selectedCurrency === 'RS' && expense.enteredRS !== undefined) {
          const rsWithTax = parseFloat(expense.enteredRS || 0) * (1 + taxPercentage / 100);
          expense.enteredRS = rsWithTax.toFixed(2);
        }
      }
      setExpenses(updatedExpenses);
    }
  };

  // Handler for changing expense type
  const handleTypeChange = (dayIndex, expenseIndex, newType) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
      updatedExpenses[dayIndex].expenses[expenseIndex].type = newType;
      setExpenses(updatedExpenses);
    }
  };

  // Function to add a new expense to a day
  const addExpense = (dayIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex]) {
      updatedExpenses[dayIndex].expenses.push({ type: "", amount: 0, baseAmount: 0, taxIncluded: false });
      setExpenses(updatedExpenses);
    }
  };

  // Function to remove an expense from a day
  const removeExpense = (dayIndex, expenseIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses.length > 1) {
      updatedExpenses[dayIndex].expenses.splice(expenseIndex, 1);
      setExpenses(updatedExpenses);
    }
  };

  // Function to save changes to the API
  const saveChanges = async () => {
    if (!token || !itineraryId) {
      onClose();
      return;
    }
    setUpdating(true);
    try {
      console.log('OtherExpensesPopup: Saving updated other expenses data:', expenses);
      
      // Get the full itinerary data from the API response to preserve other data
      const itineraryData = apiResponseData?.data?.[0] || apiResponseData || {};
      
      // Create a deep copy of the itinerary data to avoid mutating the original
      const updatedItineraryData = JSON.parse(JSON.stringify(itineraryData));
      
      // Make sure itinerary_cost array exists
      if (!updatedItineraryData.itinerary_cost) {
        updatedItineraryData.itinerary_cost = [];
      }
      
      // Process expenses by day
      expenses.forEach((dayExpense, dayIndex) => {
        const dayNumber = dayExpense.day;
        const dayString = `Day ${dayNumber.toString().padStart(2, '0')}`;
        
        // Find the corresponding day in itinerary_cost
        let dayCostIndex = updatedItineraryData.itinerary_cost.findIndex(cost => cost.day === dayString);
        
        // If day doesn't exist in itinerary_cost, create it
        if (dayCostIndex === -1) {
          // Create a new day entry with all required fields based on the API structure
          updatedItineraryData.itinerary_cost.push({
            day: dayString,
            calendar_date: dayExpense.date || "Not Selected",
            city: "", // This would ideally be populated from the itinerary data
            Other_Expenses: [],
            day_total_cost: "0.00",
            day_total_cost_with_tax: "0.00"
          });
          dayCostIndex = updatedItineraryData.itinerary_cost.length - 1;
        }
        
        // Make sure Other_Expenses exists for this day
        if (!updatedItineraryData.itinerary_cost[dayCostIndex].Other_Expenses) {
          updatedItineraryData.itinerary_cost[dayCostIndex].Other_Expenses = [];
        }
        
        // Update the Other_Expenses for this day
        updatedItineraryData.itinerary_cost[dayCostIndex].Other_Expenses = dayExpense.expenses.map((expense, expenseIndex) => {
          let amount = parseFloat(expense.baseAmount) || 0;
          if (expense.taxIncluded) {
            const taxPercentage = getTaxPercentage();
            amount = (amount * (1 + taxPercentage / 100));
          }
          // Find currency for this expense
          const currency = currencyMap[`${dayIndex}-${expenseIndex}`] || '$';
          // Calculate final_usd_amount using latest visible value
          let final_usd_amount = 0;
          if (currency === 'RS') {
            // Use enteredRS if available, otherwise fallback to amount * usdRate
            const rsVal = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) : (parseFloat(expense.amount) * usdRate);
            final_usd_amount = isNaN(rsVal) ? 0 : rsVal / usdRate;
          } else {
            final_usd_amount = parseFloat(expense.amount) || 0;
          }
          return {
            type: expense.type,
            amount: parseFloat(amount.toFixed(2)),
            final_usd_amount: parseFloat(final_usd_amount.toFixed(2)),
            taxIncluded: !!expense.taxIncluded,
            currency,
            manually_updated: true // Mark this expense as manually updated
          };
        });
        // Print the JSON for this day's expenses for debug
        console.log('OtherExpensesPopup: Saving Other_Expenses for day', dayExpense.day, JSON.stringify(updatedItineraryData.itinerary_cost[dayCostIndex].Other_Expenses, null, 2));
        
        // Calculate and update the day's total other expenses (sum of final_usd_amount)
        const dayFinalUSDTotal = updatedItineraryData.itinerary_cost[dayCostIndex].Other_Expenses.reduce((sum, expense) => {
          return sum + (parseFloat(expense.final_usd_amount) || 0);
        }, 0);
        
        // Store other expenses total as sum of final_usd_amount
        updatedItineraryData.itinerary_cost[dayCostIndex].other_expenses_total = dayFinalUSDTotal.toFixed(2);
        
        // Add other expenses to day_total_cost
        const existingDayTotal = parseFloat(updatedItineraryData.itinerary_cost[dayCostIndex].day_total_cost) || 0;
        updatedItineraryData.itinerary_cost[dayCostIndex].day_total_cost = (existingDayTotal + dayFinalUSDTotal).toFixed(2);
        
        // If day_total_cost_with_tax exists, update it with tax
        if (updatedItineraryData.itinerary_cost[dayCostIndex].day_total_cost_with_tax) {
          const taxPercentage = itineraryData.taxes?.[0]?.tax_percentage || 0;
          const newDayTotal = existingDayTotal + dayFinalUSDTotal;
          updatedItineraryData.itinerary_cost[dayCostIndex].day_total_cost_with_tax = (newDayTotal * (1 + taxPercentage / 100)).toFixed(2);
        }
      });
      
      // Calculate total other expenses for summary/reporting
      const otherExpensesGrandTotal = calculateTotal();
      
      // Add the total to the itinerary data for the Costing component to use
      if (!updatedItineraryData.total_costs) {
        updatedItineraryData.total_costs = {};
      }
      
      // Store other expenses grand total with a different variable name
      updatedItineraryData.total_costs.other_expenses_grand_total = otherExpensesGrandTotal.toFixed(2);
      
      // Prepare the payload for the API
      const apiPayload = {
        itinerary_id: itineraryId,
        updates: updatedItineraryData,
        price_change: true
      };
      
      console.log('OtherExpensesPopup: Sending API payload:', apiPayload);
      
      // Make the API request to update the itinerary data
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/update_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update other expenses: ${response.status} - ${errorData.error || "Unknown error"}`);
      }

      const responseData = await response.json();
      console.log('OtherExpensesPopup: Changes saved successfully:', responseData);
      
      // Close the popup after successful save
      onClose();
    } catch (err) {
      console.error('Error saving other expenses data:', err);
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Loading Other Expenses Data...</p>
        </div>
      </div>
    );
  }
  if (updating) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Updating Other Expenses Data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out">
        <div
          className="bg-white border border-blue-200 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] relative transform transition-all duration-300 ease-in-out overflow-hidden flex flex-col"
          style={{
            background: 'linear-gradient(to bottom right, #ffffff, #f7fbff)',
            boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5), 0 8px 10px -6px rgba(59, 130, 246, 0.3)'
          }}
        >
          {/* Close icon */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-blue-500 hover:text-blue-700 transition-colors z-10"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="p-4 border-b border-blue-200 sticky top-0 bg-white z-10 shadow-sm">
            <h2 className="text-blue-800 text-center font-bold text-xl uppercase tracking-wider">Other Expenses</h2>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden p-4">
      {loading ? (
        <div className="text-center py-4">
          <p className="text-blue-600">Loading other expenses data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-100 p-4 rounded mb-4 border border-red-300">
          <p className="text-red-700">{error}</p>
          <p className="text-sm mt-2 text-red-600">Error loading data. Please try again.</p>
        </div>
      ) : (
        <div className="overflow-auto max-h-[calc(90vh-180px)] rounded-lg shadow-inner">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-blue-100">
                <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0 w-20">
                  Day
                </th>
                <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">
                  Expenses
                </th>
                <th className="border border-blue-200 p-3 text-blue-700 text-right font-medium text-xs uppercase tracking-wider sticky top-0 w-28">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-100">
                    {expenses.map((day, dayIndex) => (
                <React.Fragment key={`day-${dayIndex}`}>
                  {day.expenses.map((expense, expenseIndex) => (
                    <tr
                      key={`expense-${dayIndex}-${expenseIndex}`}
                      className={`${expenseIndex % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-blue-100 transition-colors`}
                    >
                            {/* Only show day info on the first expense row for each day */}
                            {expenseIndex === 0 ? (
                              <td className="px-4 py-3 text-gray-800 align-top" rowSpan={day.expenses.length}>
                                <div className="font-medium">{day.day}</div>
                                <div className="text-xs text-gray-500">{day.date}</div>
                              </td>
                            ) : null}
                            <td className="px-4 py-3 text-gray-800">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={expense.type || ''}
                                  onChange={(e) => handleTypeChange(dayIndex, expenseIndex, e.target.value)}
                                  className="flex-grow px-2 py-1 border border-blue-200 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
                                  placeholder="Expense type"
                                />
                                <button
                                  onClick={() => removeExpense(dayIndex, expenseIndex)}
                                  className="text-red-500 hover:text-red-700 focus:outline-none"
                                  title="Remove expense"
                                >
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-800">
                              <div className="flex items-center justify-end space-x-2">
                                {/* Toggle switch for currency */}
                                <button
                                  type="button"
                                  onClick={() => handleCurrencyToggle(dayIndex, expenseIndex)}
                                  className={`relative w-12 h-6 flex items-center rounded-full focus:outline-none transition border border-blue-300 ${((currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$') ? 'bg-blue-200' : 'bg-green-200'}`}
                                  title="Toggle currency"
                                  style={{ minWidth: 48 }}
                                >
                                  <span className={`absolute left-1 text-xs font-bold transition-colors ${(currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$' ? 'text-blue-900' : 'text-gray-400'}`}>$</span>
                                  <span className={`absolute right-1 text-xs font-bold transition-colors ${(currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === 'RS' ? 'text-green-900' : 'text-gray-400'}`}>RS</span>
                                  <span
                                    className={`absolute transition-transform duration-200 ease-in-out bg-white h-5 w-5 rounded-full shadow ${((currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$') ? 'translate-x-0' : 'translate-x-6'}`}
                                    style={{ left: 0, top: 0.5 }}
                                  ></span>
                                </button>
                                <div className="flex flex-col">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={(() => {
                                      const isRS = (currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === 'RS';
                                      let val;
                                      if (isRS) {
                                        // For RS currency, use enteredRS if available
                                        val = expense.enteredRS !== undefined
                                          ? expense.enteredRS
                                          : (parseFloat(expense.amount || 0) * usdRate).toFixed(2);
                                      } else {
                                        // For USD currency, use amount directly
                                        val = expense.amount || '';
                                      }
                                      if (val === null || val === undefined || val === '' || isNaN(val)) return '';
                                      return String(val);
                                    })()}
                                    onChange={(e) => handleAmountChange(dayIndex, expenseIndex, e.target.value)}
                                    onFocus={() => setEditingCell({ day: dayIndex, expense: expenseIndex })}
                                    onBlur={() => setEditingCell(editingCell => (editingCell && editingCell.day === dayIndex && editingCell.expense === expenseIndex) ? null : editingCell)}
                                    onKeyDown={handleInputKeyDown}
                                    className="w-20 text-right border border-blue-200 rounded p-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
                                  />
                                  {/* Show conversion under the input, only if not editing this cell */}
                                  {(!editingCell || editingCell.day !== dayIndex || editingCell.expense !== expenseIndex) && (() => {
                                    const isRS = (currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === 'RS';
                                    
                                    // Get the correct values based on currency and tax status
                                    let rsVal, usdVal;
                                    
                                    if (isRS) {
                                      // When currency is RS
                                      rsVal = expense.enteredRS !== undefined
                                        ? parseFloat(expense.enteredRS) || 0
                                        : parseFloat(expense.amount || 0) * usdRate;
                                      usdVal = parseFloat(expense.amount || 0);
                                    } else {
                                      // When currency is USD
                                      usdVal = parseFloat(expense.amount || 0);
                                      rsVal = usdVal * usdRate;
                                    }
                                    
                                    // Format for display
                                    const usdDisplay = usdVal.toFixed(2);
                                    const rsDisplay = rsVal.toFixed(2);
                                    
                                    if (isRS) {
                                      return (
                                        <div className="flex flex-row items-center gap-2 text-xs text-gray-500 mt-1" style={{lineHeight:1}}>
                                          <span>$ {usdDisplay}</span>
                                          <span>=</span>
                                          <span>Rs {rsDisplay} / {usdRate}</span>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="flex flex-row items-center gap-2 text-xs text-gray-500 mt-1" style={{lineHeight:1}}>
                                          <span>Rs {rsDisplay}</span>
                                          <span>=</span>
                                          <span>$ {usdDisplay} × {usdRate}</span>
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                                {/* Show base amount only if not editing this cell */}
                                {expense.taxIncluded && (!editingCell || editingCell.day !== dayIndex || editingCell.expense !== expenseIndex) && (
                                  <div className="text-xs text-gray-500 mt-1 block text-right" style={{lineHeight:1}}>
                                    <span>Base: {expense.baseAmount !== undefined ? parseFloat(expense.baseAmount).toFixed(2) : ''}</span>
                                  </div>
                                )}
                                {/* Show selected currency symbol to the right */}
                                <span className="ml-1 font-semibold text-xs text-gray-700">{currencyMap[`${dayIndex}-${expenseIndex}`] || '$'}</span>
                                <label className="flex items-center ml-2 text-xs text-blue-700">
                                  <input
                                    type="checkbox"
                                    checked={!!expense.taxIncluded}
                                    onChange={() => handleTaxToggle(dayIndex, expenseIndex)}
                                    className="h-4 w-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500 ml-1"
                                  />
                                  <span className="ml-1">Tax Included</span>
                                </label>
                                {/* Show tax calculation only if not editing this cell */}
                                {expense.taxIncluded && getTaxPercentage() > 0 && (!editingCell || editingCell.day !== dayIndex || editingCell.expense !== expenseIndex) && (
                                  <span className="ml-2 text-xs text-blue-600">(incl. {getTaxPercentage()}% tax)</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {/* Add expense button for each day */}
                        <tr className="bg-gray-50">
                          <td colSpan="3" className="px-4 py-2">
                            <button
                              onClick={() => addExpense(dayIndex)}
                              className="w-full flex items-center justify-center px-3 py-1 border border-blue-300 text-blue-600 text-sm rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Add Expense
                            </button>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                    <tr className="bg-blue-100 sticky bottom-0 shadow-md">
                <td colSpan="2" className="border border-blue-200 p-3 text-blue-800 text-center font-semibold">
                  Total
                </td>
                <td className="border border-blue-200 p-3 text-blue-800 text-right font-semibold">
                  $ {formatCurrency(calculateTotal())}
                </td>
              </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Save and Cancel buttons - fixed at bottom */}
          <div className="p-4 border-t border-blue-200 flex justify-end space-x-3 bg-white sticky bottom-0 shadow-md z-10">
      <button
        onClick={onClose}
        className="px-4 py-2 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Cancel
      </button>
      <button
        onClick={saveChanges}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Save Changes
      </button>
          </div> {/* End content wrapper */}
        </div> {/* End popup container */}
      </div> {/* End overlay */}
    </>
  );
};

export default OtherExpensesPopup;
